import React from 'react';
import { observer, Observer, inject } from 'mobx-react';
import { toJS, action, reaction, autorun, observable, computed } from 'mobx';

import { Tabs } from '../component/Tabs';
import { Swiper } from '../component/Swiper';
import { Focuspoint } from '../component/Focuspoint';
import { GoogleMap, Marker, MarkerIconSVG } from '../component/GoogleMap';
import { DayPicker } from '../component/DayPicker';

import { SiteHeader } from '../component/SiteHeader';
import { SiteFooter } from '../component/SiteFooter';
import { StickInParent } from '../component/StickInParent';
import { Dropdown } from '../component/Dropdown';
import { AllotmentCalendar } from '../component/AllotmentCalendar';
import { AllotmentCalendarMonth } from '../component/AllotmentCalendarMonth';

import { ItemDescriptor } from '../component/ItemDescriptor';

import * as constants from '../../model/const';

import { fmtPrice, fmtPercent, fmtDate, fmtTime } from '../../model/formatters';

import translateBase from '../../translate';

const google = window.google;

let translateComponent = translateBase.bindComponent('dates');
let translate = translateBase.bindPage('objectDetail');

function offerDescription(offer) {
    return (
        <div className="offer-tooltip">
            {offer.description && offer.description.length > 0 && <div className="description">
                <h4>{translate("description", "Description:")}</h4>
                <p className="description-text">
                    {offer.description}
                </p>
            </div>}
            {offer.season.periods.length > 0 && <div className="season-times">
                <h4>{translate("seasonDatetimes", "Saisonzeiten:")}</h4>
                <div className="season-times-list">
                    {offer.season.periods.map(o => {
                        return <div className="season-times-item">{fmtDate(o.startDate)} &ndash; {fmtDate(o.endDate)}</div>
                    })}
                </div>
            </div>}
            <div className="pickup-info">
                <h4>{translate("pickups", "Übernahme:")}</h4>
                <div className="pickup-info-content">
                    {offer.pickupSchedule.periods.map(period => {
                        return (<div className="pickup-period">
                            <span className="pickup-period-days">
                                {period.days.map(o => translateComponent(o, o)).join(', ')}
                            </span>
                            {' '}
                            <span className="pickup-period-times">
                                {fmtTime(period.timeFrom)} &ndash; {fmtTime(period.timeTo)}
                            </span>
                        </div>)
                    })}
                </div>
            </div>
            <div className="dropoff-info">
                <h4>{translate("dropoffs", "Rückgabe:")}</h4>
                <div className="dropoff-info-content">
                    {offer.dropoffSchedule.periods.map(period => {
                        return (<div className="dropoff-period">
                            <span className="dropoff-period-days">
                                {period.days.map(o => translateComponent(o, o)).join(', ')}
                            </span>
                            {' '}
                            <span className="dropoff-period-times">
                                {fmtTime(period.timeFrom)} &ndash; {fmtTime(period.timeTo)}
                            </span>
                        </div>)
                    })}
                </div>
            </div>
            {offer.prolongation && <div className="prolongation-info">
                <h4>{translate("prolongation", "Verlängerung:")}</h4>
                <div className="prolongation-info-content">
                    <div className="prolongation-left">
                        <span className="prolongation-name">
                            {offer.prolongation.name}
                        </span>
                        <span className="prolongation-timeunit">
                            ({offer.prolongation.tuQuantity} {offer.prolongation.tuName})
                        </span>
                    </div>
                    <span className="prolongation-price">
                        {offer.prolongation.currency} {offer.prolongation.offerPrice}
                    </span>
                </div>
            </div>}

        </div>
    )
}

@inject(stores => ({
    vObjectDetails: stores.app.vObjectDetails,
    organization: stores.app.organization,
    rentalObject: stores.app.rentalObject,
    businessUnit: stores.app.businessUnit,
    aggServices: stores.app.aggServices,
    navigation: stores.app.navigation,
    allotmentDetails: stores.app.allotmentDetails,
}))
@observer
export class Detail extends React.Component {
    constructor(props) {
        super(props);
        this.pictSwiperEngine = undefined;
        this.thumbSwiperEngine = undefined;
    }

    @action.bound
    handleCalendarClicked(event) {
        if (!this.props.rentalObject.notBookableOnline) {
            this.props.navigation.startBookingWizard(event);
        } else {
            console.log("object not bookable online");
        }
    }

    tryConnectSwipers() {
        if (this.thumbSwiperEngine && this.pictSwiperEngine) {
            this.thumbSwiperEngine.params.control = this.pictSwiperEngine;
            this.pictSwiperEngine.params.control = this.thumbSwiperEngine;
        }
    }

    handleThumbSwiperEngineCreated = (eng) => {
        this.thumbSwiperEngine = eng;
        this.tryConnectSwipers();
    }

    handlePictSwiperEngineCreated = (eng) => {
        this.pictSwiperEngine = eng;
        this.tryConnectSwipers();
    }

    scrollToMap() {
        document.querySelector('.map-container').scrollIntoView({ behavior: 'smooth' });
    }

    render() {
        const { rentalObject, businessUnit, navigation, aggServices, organization, allotmentDetails, vObjectDetails } = this.props;
        const currency = businessUnit.currencyName; //"CHF";
        const MAX_ITEM_COUNT = 3;
        let consumableIdx = 0;

        let rentalObjectOffers = [];
        let rentalObjectConsumables = [];
        rentalObject.consumables.forEach(consumable =>
            consumable.offers.forEach(offer => {
                rentalObjectConsumables.push(offer);
            })
        )
        let services = [];
        let accessories = [];
        rentalObject.accessoriesWr.forEach(accessory =>
            accessory.offers.forEach(offer => {
                accessories.push(offer);
            })
        )
        let cancellationFees = [];
        var cancelHours = businessUnit.cancellationFees.map(fee => fee.minHoursPriorToPickup);
        let cancelTimeunit = Math.max.apply(Math, cancelHours) >= 48 ? 'DAYS' : 'HOURS';

        return (
            <div>

                <SiteHeader />

                <section className="topdetails-container details-view">
                    <div className="wrapper">
                        <div className="row productDetail-container" data-scroll-index='0'>
                            <div className="column-5">
                                <a onClick={navigation.goSearch} className="back-link small"><i className="fa fa-arrow-left" aria-hidden="true"></i> {translate("back", "Zurück")}</a>
                                <div className="product-details">
                                    <div className="product-name biggest">
                                        {rentalObject.name}
                                    </div>
                                    {(rentalObject.resultPriceFrom !== null) && <div className="product-price">
                                        <span className="from">{translate("fromPrice", "Ab")}</span>
                                        <span className="amount">{currency} {fmtPrice(rentalObject.resultPriceFrom)}</span>
                                        <span className="duration">/ {rentalObject.timeUnitForStartingPrice}</span>
                                    </div>}
                                    <div className="product-location">
                                        <a onClick={this.scrollToMap} data-scroll-nav='3'>
                                            <i className="fa fa-map-marker" aria-hidden="true"></i> <span>{businessUnit.name}</span>
                                        </a>
                                    </div>
                                </div>
                            </div>
                            <div className={"column-7 " + (rentalObject.pictureAttachments.length > 1 ? 'with-thumbs' : 'no-thumbs')}>

                                <Swiper config={constants.IMAGE_SWIPER_PROPS}
                                    onSwiperEngineCreated={this.handlePictSwiperEngineCreated}>{({ ref, slideRef }) =>
                                        <div className="swiper-container fullproductimage-container" ref={ref}>
                                            <div className="swiper-wrapper">
                                                {rentalObject.pictureAttachments.map(attachment =>
                                                    <div className="swiper-slide" ref={slideRef} key={attachment.id}>
                                                        <figure className="product-image">
                                                            <div className="spacer"></div>
                                                            <Focuspoint
                                                                src={attachment.pictureUrl}
                                                                imageW={1512}
                                                                imageH={830}
                                                                focusX={0.0}
                                                                focusY={0.0}
                                                                reCalcOnWindowResize={true}
                                                            />
                                                        </figure>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    }</Swiper>

                                <div className={rentalObject.pictureAttachments.length <= 1 && "hidden"}>
                                    <Swiper config={constants.THUMB_SWIPER_PROPS}
                                        onSwiperEngineCreated={this.handleThumbSwiperEngineCreated}>{({ ref, slideRef }) =>
                                            <div className="swiper-container thumbsproductimage-container" ref={ref}>
                                                <div className="swiper-wrapper">
                                                    {rentalObject.pictureAttachments.map(attachment =>
                                                        <div className="swiper-slide" ref={slideRef} key={attachment.id}>
                                                            <figure className="product-image">
                                                                <div className="spacer"></div>
                                                                <Focuspoint
                                                                    src={attachment.pictureUrl}
                                                                    imageW={756}
                                                                    imageH={415}
                                                                    focusX={0.0}
                                                                    focusY={0.0}
                                                                    reCalcOnWindowResize={true}
                                                                />
                                                            </figure>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        }</Swiper>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                <StickInParent>{({ ref: sipRef }) => <Observer>{() =>
                    <section className={"subnavi-container sticky-container" + (rentalObject.notBookableOnline ? ' not-bookable' : '')} ref={sipRef}>
                        <div className="wrapper">
                            <div className="row">
                                <div className="product-details">
                                    <div className="product-name biggest">
                                        {rentalObject.name}
                                    </div>
                                    {(rentalObject.resultPriceFrom !== null) && <div className="product-price">
                                        <span className="from">{translate("fromPrice", "Ab")}</span>
                                        <span className="amount">{currency} {fmtPrice(rentalObject.resultPriceFrom)}</span>
                                        <span className="duration">/ {rentalObject.timeUnitForStartingPrice}</span>
                                    </div>}
                                    <div className="product-location">
                                        <a data-scroll-nav='3'><i className="fa fa-map-marker" aria-hidden="true"></i> {businessUnit.name}</a>
                                    </div>
                                </div>
                                <a id="btn-book-object" className="book-object" onClick={navigation.startBookingWizard}>{translate("bookObject", "Objekt Buchen")} <i className="fa fa-shopping-cart" aria-hidden="true"></i></a>
                                <div className="not-bookable-text">{translate("notBookableOnline", "Dieses Objekt kann nicht online gebucht werden.")}</div>
                            </div>
                        </div>
                    </section>
                }</Observer>}</StickInParent>

                <section className="description-container calendar-container details-view white" data-scroll-index='1'>
                    <div className="wrapper">
                        <div className="row">
                            <div className="column-5">
                                <div className="product-description">
                                    <p className="product-claim">
                                        {rentalObject.shortDescription}
                                    </p>
                                    <p dangerouslySetInnerHTML={{ __html: rentalObject.description }}>
                                    </p>
                                </div>

                                <Dropdown>{({ ref, isOpen, onOpen, onClose }) => <Observer>{() =>
                                    <div ref={ref}>
                                        {rentalObject.properties.length > 0 && <div className="product-properties">
                                            <div className="title">
                                                {translate("properties", "Eigenschaften")}
                                            </div>
                                            <ul className="properties-list">
                                                {rentalObject.properties.map((property, idx) =>
                                                    <li className={"" + ((idx > MAX_ITEM_COUNT - 1 && !isOpen) ? " hidden" : "")}>
                                                        <div className="properties-list-item name">{property.name}</div>
                                                        <div className="properties-list-item value">{property.value ? property.value : null}</div>
                                                    </li>
                                                )}
                                            </ul>
                                            {rentalObject.properties.length > MAX_ITEM_COUNT && !isOpen &&
                                                <a className="offers-more-link" onClick={onOpen}>
                                                    <i className="fa fa-arrow-circle-down"></i> <span>{translate("showMoreProperties", "Weitere Eigenschaften anzeigen")}</span>
                                                </a>
                                            }
                                            {rentalObject.properties.length > MAX_ITEM_COUNT && isOpen &&
                                                <a className="offers-more-link" onClick={onClose}>
                                                    <i className="fa fa-times-circle"></i> <span>{translate("showLessProperties", "Schliessen")}</span>
                                                </a>
                                            }
                                        </div>}
                                    </div>
                                }</Observer>}</Dropdown>

                                <Dropdown>{({ ref, isOpen, onOpen, onClose }) => <Observer>{() =>
                                    <div ref={ref}>
                                        {rentalObject.attachments.filter(o => !o.isImage).length > 0 && <div className="product-attachments">
                                            <div className="title">
                                                {translate("attachments", "Anhänge")}
                                            </div>
                                            <ul className="attachments-list">
                                                {rentalObject.attachments.filter(o => !o.isImage).map((attachment, idx) =>
                                                    <li className={"" + ((idx > MAX_ITEM_COUNT - 1 && !isOpen) ? " hidden" : "")}>
                                                        <a href={`/api/public/RentalObject/Attachment/${attachment.id}`} target="_blank">
                                                            <i className="fa fa-file-pdf-o"></i> <span>{attachment.name || translate("unnamedAttachment", "Anhang")}</span>
                                                        </a>
                                                    </li>
                                                )}
                                            </ul>
                                            {rentalObject.attachments.filter(o => !o.isImage).length > MAX_ITEM_COUNT && !isOpen &&
                                                <a className="attachments-more-link" onClick={onOpen}>
                                                    <i className="fa fa-arrow-circle-down"></i> <span>{translate("showMoreAttachments", "Weitere Anhänge anzeigen")}</span>
                                                </a>
                                            }
                                            {rentalObject.attachments.filter(o => !o.isImage).length > MAX_ITEM_COUNT && isOpen &&
                                                <a className="attachments-more-link" onClick={onClose}>
                                                    <i className="fa fa-times-circle"></i> <span>{translate("showLessAttachments", "Schliessen")}</span>
                                                </a>
                                            }
                                        </div>}
                                    </div>
                                }</Observer>}</Dropdown>

                            </div>
                            <div className="column-7">
                                <div className="explain-text">
                                    <p className="title">{translate("availableDates", "Verfügbare Daten")}</p>
                                    <p>{translate("availableDatesDescription", "Bei Bedarf Buchungsdaten im nächsten Schritt ändern.")}</p>
                                </div>
                                <div className="product-calendar detail">
                                    <div className="calendar-btns">
                                        <button onClick={allotmentDetails.goPrevFast}><i className="fa fa-angle-double-left" aria-hidden="true"></i></button>
                                        <button onClick={allotmentDetails.goPrev}><i className="fa fa-chevron-left" aria-hidden="true"></i></button>
                                        <button onClick={vObjectDetails.changeViewType}>
                                            {allotmentDetails.calendarView === "month" ? translate("weekView", "Wochenansicht") : translate("monthView", "Monatsansicht")}
                                        </button>
                                        {/*<button onClick={allotmentDetails.goToday}>Today</button>*/}
                                        <button onClick={vObjectDetails.goSelection}>{translate("yourChoice", "Ihre Auswahl")}</button>
                                        <button onClick={allotmentDetails.goNext}><i className="fa fa-chevron-right" aria-hidden="true"></i></button>
                                        <button onClick={allotmentDetails.goNextFast}><i className="fa fa-angle-double-right" aria-hidden="true"></i></button>
                                    </div>
                                    <div className="calendar-main" onClick={this.handleCalendarClicked}>
                                        {allotmentDetails.calendarView !== 'month' ?
                                            <AllotmentCalendar
                                                selectableAllotment={false}
                                                movableAllotment={false}
                                                observables={observable({
                                                    selection: {
                                                        start: computed(() => vObjectDetails.selectionStartDate),
                                                        end: computed(() => vObjectDetails.selectionEndDate),
                                                    },
                                                    /*selected: {},*/
                                                    updtItems: computed(() => allotmentDetails.updtItems),
                                                    alreadyRentItems: computed(() => allotmentDetails.alreadyRentItems),
                                                    defaultView: computed(() => allotmentDetails.calendarView),
                                                    defaultDate: computed(() => allotmentDetails.calendarDefaultDate),
                                                })}
                                            />
                                            :
                                            <AllotmentCalendarMonth
                                                selectableAllotment={false}
                                                defaultDate={allotmentDetails.calendarDefaultDate}
                                                unavailable={allotmentDetails.alreadyRentItems}
                                                updt={allotmentDetails.updtItems}
                                                selection={observable({
                                                    start: computed(() => vObjectDetails.selectionStartDate),
                                                    end: computed(() => vObjectDetails.selectionEndDate),
                                                })}
                                            />
                                        }
                                    </div>
                                    <div className="calendar-infos">
                                        <div className="calendar-legend">
                                            <div className="legend-row selected-booking">
                                                <div className="legend-color"></div>
                                                <div className="legend-label">{translate("yourChoice", "Ihre Auswahl")}</div>
                                            </div>
                                            <div className="legend-row booked">
                                                <div className="legend-color"></div>
                                                <div className="legend-label">{translate("notAvailable", "Nicht verfügbar")}</div>
                                            </div>
                                            <div className="legend-row pickup-drop">
                                                <div className="legend-color"></div>
                                                <div className="legend-label">{translate("pickupDrop", "Übernahme- & Rückgabezeiten")}</div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                <section className="offers-container details-view" data-scroll-index='2'>
                    <div className="wrapper">
                        <div className="row">
                            <div className="column-12">
                                <Tabs>{({ ulRef, liRef }) => <Observer>{() => {
                                    return <div className="tab-container">
                                        <ul className="tab tab_hover animated" data-tab ref={ulRef}>
                                            {rentalObject.offers.length > 0 &&
                                                <li ref={liRef} className="tab_title tab_title_active"> <a href="#tab-1">{translate("offers", "Angebote")}</a> </li>
                                            }
                                            {rentalObject.consumables.length > 0 &&
                                                <li ref={liRef} className="tab_title"> <a href="#tab-2">{translate("consumables", "Nutzung")}</a> </li>
                                            }
                                            {aggServices.avail.length > 0 &&
                                                <li ref={liRef} className="tab_title"> <a href="#tab-3">{translate("services", "Dienstleistungen")}</a> </li>
                                            }
                                            {accessories.length > 0 &&
                                                <li ref={liRef} className="tab_title"> <a href="#tab-4">{translate("accessories", "Zubehör")}</a> </li>
                                            }
                                            {businessUnit.cancellationFees.length > 0 &&
                                                <li ref={liRef} className="tab_title"> <a href="#tab-5">{translate("cancellationFees", "Storno-Gebühren")}</a> </li>
                                            }
                                            <li className="tab_bar"></li>
                                        </ul>
                                        <div className="tab-content">
                                            <Dropdown>{({ ref, isOpen, onOpen, onClose }) => <Observer>{() =>
                                                <div className="tab-content_page" id="tab-1" ref={ref}>
                                                    <div className="offers-entry">
                                                        {rentalObject.sortedOffers.length === 0 &&
                                                            <p>{translate("noOfferItems", "There are no items.")}</p>
                                                        }
                                                        {rentalObject.sortedOffers.map((offer, idx) =>
                                                            <div className={"list-entry" + ((idx > MAX_ITEM_COUNT - 1 && !isOpen) ? " hidden" : "")}>
                                                                <div className="input">
                                                                    <ItemDescriptor html={offerDescription(offer)}>
                                                                        {offer.name}
                                                                    </ItemDescriptor>
                                                                </div>
                                                                <div className="value">{currency} {fmtPrice(offer.price)}</div>
                                                                <div className="amount"><input /><a className="minus"><i className="fa fa-minus"></i></a><a className="plus"><i className="fa fa-plus"></i></a></div>
                                                                <div className="price">{currency} {fmtPrice(offer.price)}</div>
                                                            </div>
                                                        )}
                                                    </div>
                                                    {rentalObject.sortedOffers.length > MAX_ITEM_COUNT && !isOpen &&
                                                        <a className="offers-more-link" onClick={onOpen}>
                                                            <i className="fa fa-arrow-circle-down"></i> <span>{translate("showMoreOffers", "Weitere Angebote anzeigen")}</span>
                                                        </a>
                                                    }
                                                    {rentalObject.sortedOffers.length > MAX_ITEM_COUNT && isOpen &&
                                                        <a className="offers-more-link" onClick={onClose}>
                                                            <i className="fa fa-times-circle"></i> <span>{translate("showLessOffers", "Schliessen")}</span>
                                                        </a>
                                                    }
                                                </div>
                                            }</Observer>}</Dropdown>
                                            <Dropdown>{({ ref, isOpen, onOpen, onClose }) => <Observer>{() =>
                                                <div className="tab-content_page" id="tab-2" ref={ref}>
                                                    {rentalObject.consumables.length === 0 ?
                                                        <p>{translate("noConsumables", "There are no items.")}</p>
                                                        :
                                                        <div className="consumables-info">{translate("consumablesTabDescription", "Inbegriffene und zusätzliche Nutzungskosten")}</div>
                                                    }

                                                    {rentalObject.consumables.map((consumable, idx1) =>
                                                        <div className="consumables-entry">
                                                            <div className="consumables-title">
                                                                {consumable.name}
                                                            </div>
                                                            {consumable.sortedOffers.map((offer, idx2) =>
                                                                <div className={"list-entry" + (((idx1 * consumable.offers.length + idx2) > MAX_ITEM_COUNT - 1 && !isOpen) ? " hidden" : "")}>
                                                                    <div className="input">{offer.name}</div>
                                                                    <div className="value">{offer.freeUnitQuantity} {consumable.unitName}</div>
                                                                    <div className="amount"><input /><a className="minus"><i className="fa fa-minus"></i></a><a className="plus"><i className="fa fa-plus"></i></a></div>
                                                                    <div className="price">{offer.freeUnitQuantity} {consumable.unitName}</div>
                                                                </div>
                                                            )}
                                                            <div className="consumables-note">
                                                                {consumable.minQuantity > 0 && <div className="minimum">{translate("consumableMinimum", "mindestens")} {consumable.minQuantity} {consumable.unitName}</div>}
                                                                {currency} {fmtPrice(consumable.pricePerUnit)} {translate("consumableFooterDescription", "pro zusätzliche(n)")} {consumable.unitName}
                                                            </div>
                                                        </div>
                                                    )}
                                                    {rentalObject.consumables.length > MAX_ITEM_COUNT && !isOpen &&
                                                        <a className="consumables-more-link" onClick={onOpen}>
                                                            <i className="fa fa-arrow-circle-down"></i> <span>{translate("showMoreConsumables", "Weitere Nutzungskosten anzeigen")}</span>
                                                        </a>
                                                    }
                                                    {rentalObject.consumables.length > MAX_ITEM_COUNT && isOpen &&
                                                        <a className="consumables-more-link" onClick={onClose}>
                                                            <i className="fa fa-times-circle"></i> <span>{translate("showLessConsumables", "Schliessen")}</span>
                                                        </a>
                                                    }
                                                </div>
                                            }</Observer>}</Dropdown>
                                            <Dropdown>{({ ref, isOpen, onOpen, onClose }) => <Observer>{() =>
                                                <div className="tab-content_page" id="tab-3" ref={ref}>
                                                    <div className="services-entry">
                                                        {aggServices.avail.length === 0 &&
                                                            <p>{translate("noServices", "There are no items.")}</p>
                                                        }
                                                        {aggServices.avail.map((service, idx) =>
                                                            <div className={"list-entry" +
                                                                ((idx > MAX_ITEM_COUNT - 1 && !isOpen) ? " hidden" : "") +
                                                                ((service.price < 0) ? " discount" : "")}>
                                                                <div className="input">
                                                                    <ItemDescriptor description={service.description}>{service.name}</ItemDescriptor>
                                                                    {(service.price < 0 && service.typeId === constants.SERVICE_TYPE.OBLIGATORISCH) &&
                                                                        <span>{translate("discount", "Rabatt")}</span>
                                                                    }
                                                                </div>
                                                                <div className="value">{!service.percentagePrice ? `${currency} ${fmtPrice(service.price)}` : `${fmtPercent(service.percentagePrice)}%`}</div>
                                                                <div className="amount"><input /><a className="minus"><i className="fa fa-minus"></i></a><a className="plus"><i className="fa fa-plus"></i></a></div>
                                                                <div className="price">{currency} {fmtPrice(service.price)}</div>
                                                            </div>
                                                        )}
                                                    </div>
                                                    {aggServices.avail.length > MAX_ITEM_COUNT && !isOpen &&
                                                        <a className="services-more-link" onClick={onOpen}>
                                                            <i className="fa fa-arrow-circle-down"></i> <span>{translate("showMoreServices", "Weitere Dienstleistungen anzeigen")}</span>
                                                        </a>
                                                    }
                                                    {aggServices.avail.length > MAX_ITEM_COUNT && isOpen &&
                                                        <a className="services-more-link" onClick={onClose}>
                                                            <i className="fa fa-times-circle"></i> <span>{translate("showLessServices", "Schliessen")}</span>
                                                        </a>
                                                    }
                                                </div>
                                            }</Observer>}</Dropdown>
                                            <Dropdown>{({ ref, isOpen, onOpen, onClose }) => <Observer>{() =>
                                                <div className="tab-content_page" id="tab-4" ref={ref}>
                                                    <div className="accessories-entry">
                                                        {accessories.length === 0 && // TODO: Proper count
                                                            <p>{translate("noAccessories", "There are no items.")}</p>
                                                        }
                                                        {rentalObject.accessoriesWr.map((accessory, idx1) =>
                                                            <div className="list-entry-wrapper">
                                                                {accessory.offers.map((offer, idx2) => {
                                                                    return <div className={"list-entry" + (((idx1 * accessory.offers.length + idx2) > MAX_ITEM_COUNT - 1 && !isOpen) ? " hidden" : "")}>
                                                                        <div className="input"><ItemDescriptor description={accessory.description}>{accessory.name} - {offer.name}</ItemDescriptor></div>
                                                                        <div className="value">{currency} {fmtPrice(offer.price)}</div>
                                                                        <div className="amount"><input /><a className="minus"><i className="fa fa-minus"></i></a><a className="plus"><i className="fa fa-plus"></i></a></div>
                                                                        <div className="price">{currency} {fmtPrice(offer.price)}</div>
                                                                    </div>
                                                                })}
                                                            </div>
                                                        )}
                                                    </div>
                                                    {accessories.length > MAX_ITEM_COUNT && !isOpen &&
                                                        <a className="accessories-more-link" onClick={onOpen}>
                                                            <i className="fa fa-arrow-circle-down"></i> <span>{translate("showMoreAccessories", "Weitere Zubehör anzeigen")}</span>
                                                        </a>
                                                    }
                                                    {accessories.length > MAX_ITEM_COUNT && isOpen &&
                                                        <a className="accessories-more-link" onClick={onClose}>
                                                            <i className="fa fa-times-circle"></i> <span>{translate("showLessAccessories", "Schliessen")}</span>
                                                        </a>
                                                    }
                                                </div>
                                            }</Observer>}</Dropdown>
                                            <Dropdown>{({ ref, isOpen, onOpen, onClose }) => <Observer>{() =>
                                                <div className="tab-content_page" id="tab-5" ref={ref}>
                                                    <div className="cancelfee-entry">
                                                        {businessUnit.cancellationFees.length === 0 ?
                                                            <p>{translate("noCancellationFees", "There are no items.")}</p>
                                                            :
                                                            <div className="list-entry header">
                                                                {cancelTimeunit=='DAYS' && <div className="day">{translate("daysToPickup", "Tage bis zur Übernahme")}</div>}
                                                                {cancelTimeunit=='HOURS' && <div className="day">{translate("hoursToPickup", "Stunden bis zur Übernahme")}</div>}
                                                                <div className="percent">{translate("percent", "Prozent")}</div>
                                                                <div className="fix">{translate("fix", "Fix")}</div>
                                                            </div>
                                                        }
                                                        {businessUnit.cancellationFees.map((cancellationFee, idx) =>
                                                            <div className={"list-entry" + ((idx > MAX_ITEM_COUNT - 1 && !isOpen) ? " hidden" : "")}>
                                                                <div className="day">{cancelTimeunit=='DAYS' ? cancellationFee.minHoursPriorToPickup/24 : cancellationFee.minHoursPriorToPickup}</div>
                                                                <div className="percent">{cancellationFee.rate}%</div>
                                                                <div className="fix">{currency} {fmtPrice(cancellationFee.fix)}</div>
                                                            </div>
                                                        )}
                                                    </div>
                                                    {businessUnit.cancellationFees.length > MAX_ITEM_COUNT && !isOpen &&
                                                        <a className="cancelfee-more-link" onClick={onOpen}>
                                                            <i className="fa fa-arrow-circle-down"></i> <span>{translate("showMoreCancellationFees", "Weitere Gebühren anzeigen")}</span>
                                                        </a>
                                                    }
                                                    {businessUnit.cancellationFees.length > MAX_ITEM_COUNT && isOpen &&
                                                        <a className="cancelfee-more-link" onClick={onClose}>
                                                            <i className="fa fa-times-circle"></i> <span>{translate("showLessCancellationFees", "Schliessen")}</span>
                                                        </a>
                                                    }
                                                </div>
                                            }</Observer>}</Dropdown>
                                        </div>
                                    </div>
                                }}</Observer>}</Tabs>
                            </div>
                        </div>
                    </div>
                </section>

                <section className="map-container" data-scroll-index='3'>
                    <div className="nowrapper">
                        <div className="row">
                            <div className="column-12">
                                {businessUnit.canShowAddress && <div className="map-address-wrapper">
                                    <div className="map-address">
                                        <strong className="big">{businessUnit.name}</strong><br />
                                        {businessUnit.postAddress.street} <br />
                                        {businessUnit.postAddress.postalCode} {businessUnit.postAddress.city}<br />
                                        {businessUnit.phone && <span>Tel: {businessUnit.phone}<br /></span>}
                                        <a href={`mailto:${businessUnit.email}`}>{businessUnit.email}</a>
                                    </div>
                                </div>}
                                <Observer>{() => businessUnit.lat && businessUnit.lon ?
                                    <GoogleMap
                                        containerElement={<div className="q" />}
                                        mapElement={<div id="map"></div>}
                                        zoom={11}
                                        center={{ lat: businessUnit.lat, lng: businessUnit.lon }}
                                        {...{
                                            zoom: 13,
                                            mapTypeControl: false,
                                            scrollwheel: false,
                                            streetViewControl: false,
                                            navigationControl: true,
                                            zoomControl: true,
                                            panControl: true,
                                            zoomControlOptions: {
                                                style: google.maps.ZoomControlStyle.SMALL
                                            },
                                            mapTypeId: google.maps.MapTypeId.ROADMAP
                                        }}
                                        defaultOptions={{
                                            styles: [
                                                {
                                                    stylers: [
                                                        { visibility: "on" },
                                                        { saturation: -100 },
                                                    ]
                                                }
                                            ]
                                        }}
                                    >
                                        <Marker
                                            position={{ lat: businessUnit.lat, lng: businessUnit.lon }}
                                            title="title"
                                            icon={{
                                                path: MarkerIconSVG,
                                                fillColor: organization.settings.primaryColor || '#000000',
                                                fillOpacity: 1,
                                                strokeWeight: 2,
                                                size: new google.maps.Size(30, 40),
                                                origin: new google.maps.Point(0, 0),
                                                anchor: new google.maps.Point(15, 40),
                                                scaledSize: new google.maps.Size(30, 40),
                                            }}
                                        />
                                    </GoogleMap> : null}</Observer>
                            </div>
                        </div>
                    </div>
                </section>

                <SiteFooter />
            </div >
        )
    }
}


// WEBPACK FOOTER //
// ./src/visual/view/Detail.js